import React, { useEffect, useRef, useState } from 'react'
import { Row, Col, Form } from 'react-bootstrap'
import ButtonGroup from '@components/common/ButtonGroup/ButtonGroup.component'
import Button from '@components/common/Button/Button.component'
import CreateExistingChartModal from '@components/pages/Charting/Modals/CreateExistingChartModal'
import CompareChartsModal from '@components/pages/Charting/Modals/CompareChartsModal'
import classNames from 'classnames'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { tabButtons } from '@constants/Charting'
import {
  setDentalUpdated,
  setPerioLocked,
  setPerioUpdated,
  setSelectedDentalChart,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import ChartingResetChartModal from '@components/pages/Charting/Modals/ChartingResetChartModal.component'
import ChartingAddChartModal from '@components/pages/Charting/Modals/ChartingAddChartModal.component'
import PrintPdfModal from '@components/pages/Charting/Modals/PrintPdfModal'
import {
  CHARTING_LAYER_KEYS,
  type ChartingLayersState,
} from '@utils/default-charting-view.utils'
import { ChartingLayerKey } from '@dataTypes/chartingPreferences.type'
import type { ChartAutoSaveStatus } from '@utils/chart-save.utils'

type TabType = 'home' | 'perio' | 'treatment-plan' | 'history'

interface ChartingTopBarProps {
  currentTab: TabType
  onTabChange: (tab: string) => void
  layers: ChartingLayersState
  onLayerChange: (layer: ChartingLayerKey) => void
  chartIndex: number
  onChartToggle: (direction: 'left' | 'right') => void
  isMicOn: boolean
  onMicToggle: () => void
  micDisabled: boolean
  micStatus: string
  isFullscreen: boolean
  setIsFullscreen: React.Dispatch<React.SetStateAction<boolean>>
  onAddChart: () => void
  onSaveChart: () => void
  onResetChart: () => void
  onViewNote: (id: string | null) => void
  clearSelection: () => void
  onAddTreatmentPlan?: (() => void) | null
  disableAddTreatmentPlan?: boolean
  isXrayPanelOpen: boolean
  onXrayPanelToggle: () => void
  autoSaveEnabled?: boolean
  autoSaveStatus?: ChartAutoSaveStatus
  canFinishEditing?: boolean
}

const ChartingTopBar: React.FC<ChartingTopBarProps> = ({
  currentTab,
  onTabChange,
  layers,
  onLayerChange,
  chartIndex,
  onChartToggle,
  isMicOn,
  onMicToggle,
  micDisabled,
  micStatus,
  isFullscreen,
  setIsFullscreen,
  onAddChart,
  onSaveChart,
  onResetChart,
  onViewNote,
  clearSelection,
  onAddTreatmentPlan,
  disableAddTreatmentPlan = false,
  isXrayPanelOpen,
  onXrayPanelToggle,
  autoSaveEnabled = false,
  autoSaveStatus = 'idle',
  canFinishEditing = false,
}) => {
  const dispatch = useAppDispatch()
  const compareCharts = useAppSelector((state) => state.charting.compareCharts)
  const isPerioLocked = useAppSelector((state) => state.charting.isPerioLocked)
  const isPerioTabLocked = currentTab === 'perio' && isPerioLocked
  const xrayPanelSwitchRef = useRef<HTMLInputElement>(null)

  const isPerioUpdated = useAppSelector(
    (state) => state.charting.isPerioUpdated,
  )
  const isDentalUpdated = useAppSelector(
    (state) => state.charting.isDentalUpdated,
  )
  const selectedChart = useAppSelector((state) =>
    currentTab === 'home'
      ? state.charting.selectedDentalChart
      : state.charting.selectedPerioChart,
  )
  const isUpdated = useAppSelector((state) =>
    currentTab === 'perio'
      ? state.charting.isPerioUpdated
      : state.charting.isDentalUpdated,
  )

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCompareModal, setShowCompareModal] = useState(false)
  const [showPDFModal, setShowPDFModal] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [showAddConfirm, setShowAddConfirm] = useState(false)

  const handleLockClick = (): void => {
    dispatch(setPerioLocked(!isPerioLocked))
  }

  const handleSelect = (args: { item: { text: string } }): void => {
    const selectedText = args.item.text

    switch (selectedText) {
      case 'Create from an existing chart':
        setShowCreateModal(true)
        break
      case 'Compare charts':
        setShowCompareModal(true)
        break
      case 'Print chart':
        setShowPDFModal(true)
        break
      case 'Email chart':
        // TODO: Implement email chart functionality
        break
      default:
        break
    }
  }

  const handleRefreshChart = (): void => {
    setShowResetConfirm(true)
  }

  const handleAddChart = (): void => {
    if (currentTab === 'perio' && isPerioUpdated) {
      setShowAddConfirm(true)
      return
    }
    if (currentTab === 'home' && isDentalUpdated) {
      setShowAddConfirm(true)
      return
    }
    onAddChart()
  }

  // Items based on active tab
  const getItems = (): Array<{ text: string; disabled: boolean }> => {
    if (currentTab === 'treatment-plan') {
      return [
        {
          text: 'Print chart',
          disabled: false,
        },
        {
          text: 'Email chart',
          disabled: true,
        },
      ]
    }
    if (currentTab === 'perio') {
      return [
        {
          text: 'Create from an existing chart',
          disabled: false,
        },
        {
          text: 'Compare charts',
          disabled: false,
        },
        {
          text: 'Print chart',
          disabled: false,
        },
        {
          text: 'Email chart',
          disabled: true,
        },
      ]
    }
    // home tab
    return [
      {
        text: 'Create from an existing chart',
        disabled: false,
      },
      {
        text: 'Compare charts',
        disabled: false,
      },
      {
        text: 'Print chart',
        disabled: false,
      },
      {
        text: 'Email chart',
        disabled: true,
      },
    ]
  }

  const items = getItems()

  const isSaveEnabled = (isUpdated || canFinishEditing) && !isPerioTabLocked
  const saveTooltip = isPerioTabLocked
    ? 'Chart is locked'
    : isUpdated
      ? canFinishEditing
        ? 'Save and finish editing'
        : 'Save changes'
      : canFinishEditing
        ? 'Finish editing'
        : 'No changes to save'

  useEffect(() => {
    if (!!compareCharts && !layers.perio) {
      onLayerChange('perio')
    }
    if (!!compareCharts && currentTab !== 'home') {
      onTabChange('home')
    }
  }, [compareCharts])

  return (
    <Row className="align-items-center justify-content-between charting-top-bar">
      <Col md="auto" className="d-flex align-items-center gap-2">
        <ButtonGroup
          buttons={tabButtons}
          activeTab={currentTab}
          onButtonClick={onTabChange}
        />
        {currentTab !== 'history' && <div className="icon-divider" />}
        {currentTab === 'perio' && (
          <div
            className={`d-flex align-items-center gap-1 ${
              isFullscreen ? 'disabled-icons' : ''
            }`}
          >
            <div
              className="arrow-icon-buttons d-flex align-items-center gap-1"
              style={
                isFullscreen ? { pointerEvents: 'none', opacity: 0.4 } : {}
              }
            >
              <div
                className="icon-next-wrap"
                title="Upper Arch"
                onClick={() => !isFullscreen && onChartToggle('left')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke={chartIndex === 0 ? '#97C9F9' : '#163DA2'}
                  strokeWidth="2"
                >
                  <path
                    d="M10 12L6 8L10 4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div
                className="icon-next-wrap"
                title="Lower Arch"
                onClick={() => !isFullscreen && onChartToggle('right')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke={chartIndex === 1 ? '#97C9F9' : '#163DA2'}
                  strokeWidth="2"
                >
                  <path
                    d="M6 4L10 8L6 12"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div
              className={`icon-wrap ${isFullscreen || !isUpdated ? 'disabled pe-none' : ''}`}
              title={!isPerioLocked ? 'Lock chart' : 'Unlock chart'}
              onClick={handleLockClick}
            >
              <i
                className={!isPerioLocked ? 'mgc_lock_fill' : 'mgc_unlock_fill'}
              />
            </div>
          </div>
        )}
        {currentTab === 'home' && (
          <div className="charting-button-group d-flex">
            {CHARTING_LAYER_KEYS.map((layer) => {
              const isChecked = layers[layer]

              return (
                <Form.Check
                  key={layer}
                  type="checkbox"
                  id={`layer-${layer}`}
                  label={layer.charAt(0).toUpperCase() + layer.slice(1)}
                  checked={isChecked}
                  onChange={() => onLayerChange(layer)}
                  className="me-3"
                />
              )
            })}
          </div>
        )}
      </Col>

      <Col md="auto">
        <div className="icon-buttons-container">
          {autoSaveEnabled &&
          currentTab !== 'treatment-plan' &&
          currentTab !== 'history' ? (
            <span
              className={`charting-autosave-status-slot charting-autosave-status${
                autoSaveStatus !== 'idle'
                  ? ` charting-autosave-status--${autoSaveStatus}`
                  : ''
              }`}
              aria-live="polite"
            >
              {autoSaveStatus === 'saving'
                ? 'Saving...'
                : autoSaveStatus === 'saved'
                  ? 'Saved'
                  : autoSaveStatus === 'error'
                    ? 'Save failed'
                    : ''}
            </span>
          ) : null}
          {(currentTab === 'home' || currentTab === 'treatment-plan') && (
            <div
              className="d-inline-flex align-items-center me-2"
              onMouseUp={(event: React.MouseEvent): void => {
                if (event.button !== 0) return
                /* Label clicks activate the hidden checkbox but skip its onClick; blur after pointer release. Keyboard (Tab + Space) has no matching mouse button. */
                requestAnimationFrame(() => {
                  xrayPanelSwitchRef.current?.blur()
                })
              }}
            >
              <Form.Check
                ref={xrayPanelSwitchRef}
                type="switch"
                id="charting-xray-panel-toggle"
                label="Image viewer panel"
                checked={isXrayPanelOpen}
                onChange={() => {
                  onXrayPanelToggle()
                }}
                className="charting-xray-panel-switch text-nowrap"
              />
            </div>
          )}
          {currentTab !== 'history' && currentTab !== 'treatment-plan' ? (
            <button
              type="button"
              className={`icon-wrap perio-voice-mic ${isMicOn ? 'text-danger' : ''}`}
              disabled={micDisabled}
              onClick={onMicToggle}
              aria-label={
                isMicOn ? 'Stop voice assistant' : 'Start voice assistant'
              }
              aria-pressed={isMicOn}
              title={
                micDisabled
                  ? currentTab === 'home'
                    ? 'Select an editable Dental chart'
                    : currentTab === 'perio'
                      ? 'Select an editable, unlocked Perio chart'
                      : 'Voice assistant is unavailable'
                  : `Voice assistant: ${micStatus}`
              }
            >
              <i className={isMicOn ? 'mgc_stop_fill' : 'mgc_mic_fill'} />
            </button>
          ) : null}
          {currentTab !== 'treatment-plan' && currentTab !== 'history' ? (
            <>
              <div
                className={`icon-wrap ${!selectedChart ? 'disabled pe-none' : ''}`}
                title="Add note"
                onClick={() => onViewNote(selectedChart?.chart_id ?? '')}
              >
                <i className="mgc_document_fill" />
              </div>
              <div className="icon-divider" />
              <div
                className={`icon-wrap ${isPerioTabLocked || !isUpdated ? 'disabled pe-none' : ''}`}
                title="Refresh"
                onClick={handleRefreshChart}
              >
                <i className="mgc_refresh_anticlockwise_1_line" />
              </div>
              <div title={saveTooltip} style={{ display: 'inline-block' }}>
                <div
                  className={classNames('icon-wrap', 'icon-wrap-blue', {
                    'disabled pe-none': !isSaveEnabled,
                    'has-unsaved-badge': isUpdated,
                  })}
                  onClick={onSaveChart}
                  style={{ position: 'relative' }}
                >
                  <i className="mgc_save_2_line" />
                  {isUpdated && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-3px',
                        right: '-3px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#dc3545',
                        borderRadius: '50%',
                      }}
                    />
                  )}
                </div>
              </div>

              <div className="icon-divider" />
              <Button
                size="sm"
                className={`button-primary button-add-a-chart ${isPerioTabLocked ? 'pe-none' : ''}`}
                onClick={handleAddChart}
                disabled={isPerioTabLocked}
              >
                Add chart
              </Button>
            </>
          ) : null}

          {currentTab !== 'treatment-plan' && currentTab !== 'history' ? (
            <div className="icon-wrap position-relative">
              <DropDownButtonComponent
                className={classNames('charting-options-dropdown', {
                  disabled: isPerioTabLocked,
                })}
                items={items}
                iconCss="mgc_settings_3_fill"
                iconPosition="Top"
                cssClass="popup-bottom-right"
                select={handleSelect}
                disabled={isPerioTabLocked}
              />
            </div>
          ) : currentTab === 'treatment-plan' ? (
            <>
              <Button
                size="sm"
                className="button-primary"
                onClick={() => {
                  if (onAddTreatmentPlan) {
                    onAddTreatmentPlan()
                  }
                }}
                disabled={disableAddTreatmentPlan}
              >
                Add plan
              </Button>
              <div className="icon-wrap position-relative">
                <DropDownButtonComponent
                  className="charting-options-dropdown"
                  items={items}
                  iconCss="mgc_settings_3_fill"
                  iconPosition="Top"
                  cssClass="popup-bottom-right"
                  select={handleSelect}
                  disabled={false}
                />
              </div>
            </>
          ) : null}

          {currentTab !== 'treatment-plan' && currentTab !== 'history' && (
            <>
              <div
                className="icon-wrap"
                onClick={() => setIsFullscreen((prev) => !prev)}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                <i
                  className={
                    isFullscreen
                      ? 'mgc_fullscreen_exit_line'
                      : 'mgc_fullscreen_line'
                  }
                />
              </div>
              <div className="icon-wrap" title="Info">
                <i className="mgc_information_fill" />
              </div>
            </>
          )}
        </div>
      </Col>
      <CreateExistingChartModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false)
          setSearchTerm('')
          clearSelection()
          dispatch(
            currentTab === 'perio'
              ? setSelectedPerioChart(null)
              : setSelectedDentalChart(null),
          )
        }}
        chartType={currentTab === 'perio' ? 'perio' : 'dental'}
        searchTerm={searchTerm}
        onSearchChange={(val) => setSearchTerm(val)}
      />

      <CompareChartsModal
        show={showCompareModal}
        onHide={() => setShowCompareModal(false)}
      />

      <PrintPdfModal
        show={showPDFModal}
        onHide={() => setShowPDFModal(false)}
      />

      <ChartingResetChartModal
        show={showResetConfirm}
        onCancel={() => setShowResetConfirm(false)}
        onReset={() => {
          setShowResetConfirm(false)
          onResetChart()
        }}
      />

      <ChartingAddChartModal
        show={showAddConfirm}
        onCancel={() => setShowAddConfirm(false)}
        onDiscardAndContinue={() => {
          setShowAddConfirm(false)
          onAddChart()
          if (currentTab === 'perio') {
            dispatch(setPerioUpdated(false))
          }
          if (currentTab === 'home') {
            dispatch(setDentalUpdated(false))
          }
        }}
      />
    </Row>
  )
}

export default ChartingTopBar
